import { useMemo } from 'react';
import styled from 'styled-components';

import { UK_COUNTRY } from '@lib/utils/constants';
import {
  APPLICATION_GENDER_OPTIONS,
  UK_ETHNICITY_OPTIONS,
  US_ETHNICITY_OPTIONS,
} from '@lib/utils/constants/applicationConstants';

type PostSecondaryInfoProps = {
  post: JobsBoardApplication;
  hasSplitView?: boolean;
};
export const PostSecondaryInfo = ({
  post,
  hasSplitView,
}: PostSecondaryInfoProps) => {
  const genderDisplay = useMemo(() => {
    if (!post?.genders?.length) {
      return 'Any';
    }
    return (
      APPLICATION_GENDER_OPTIONS?.filter((option) =>
        post?.genders?.includes?.(option.value)
      )
        ?.map((option) => option?.label)
        ?.join(', ') || 'Any'
    );
  }, [post?.genders]);

  const ageRangeDisplay = useMemo(() => {
    if (
      !(Number.isFinite(post?.ages?.min) && Number.isFinite(post?.ages?.max))
    ) {
      return 'Any';
    }
    return `${post?.ages?.min}-${post?.ages?.max}`;
  }, [post?.ages]);

  const ethnicityDisplay = useMemo(() => {
    if (!post?.ethnicities?.length) {
      return 'Any';
    }
    if (post?.project_location?.country === UK_COUNTRY) {
      return UK_ETHNICITY_OPTIONS.filter((option) =>
        post?.ethnicities?.includes(option)
      ).join(', ');
    }

    return (
      US_ETHNICITY_OPTIONS.filter((option) =>
        post?.ethnicities?.includes(option)
      ).join(', ') || 'Any'
    );
  }, [post?.ethnicities, post?.project_location?.country]);

  const itemsWithLabels = [
    {
      label: 'Gender:',
      value: genderDisplay,
    },
    {
      label: 'Age:',
      value: ageRangeDisplay,
    },
    {
      label: 'Ethnicity:',
      value: ethnicityDisplay,
    },
  ];

  return (
    <StyledContainer hasSplitView={hasSplitView}>
      {itemsWithLabels.map((item, index) => {
        return (
          <div
            className='item-info-with-label'
            key={`item-without-icon-${index}`}
          >
            <span className='item-info-label'>{item.label}</span>
            <span className='item-info-text'>{item.value}</span>
          </div>
        );
      })}
    </StyledContainer>
  );
};

type StyledContainerProps = {
  hasSplitView?: boolean;
};

export const StyledContainer = styled.div<StyledContainerProps>`
  line-height: 20px;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: ${({ hasSplitView }) => (hasSplitView ? 'calc(50% - 16px)' : '100%')};

  .item-info-with-label {
    display: flex;
    align-items: flex-start;
    gap: 4px;

    .item-info-label {
      color: ${({ theme }) => theme.palette.text[9]};
      font-weight: 600;
    }

    .item-info-text {
      width: auto;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px;
    line-height: 24px;
  }
`;
